import React, { useEffect, useState } from "react";
import {
  SearchIcon,
  IncomingIcon,
  CompletedIcon,
  RejectedIcon,
  PendingIcon,
} from "../../assets/svgs/Icons";
import { FiFilter } from "react-icons/fi";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Tab,
  Table,
  TableCaption,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  Link,
  Select,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { getIncomingApplications } from "./AuditorSlice";
import {
  Column,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  sortingFns,
  getSortedRowModel,
  FilterFn,
  SortingFn,
  ColumnDef,
  flexRender,
  FilterFns,
  createColumnHelper,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import moment from "moment/moment";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import Card from "./add-card";

function CancelledAplications() {
  const [status, setStatus] = useState("idle");
  const [columnFilters, setColumnFilters] = useState([]);
  const [updatedData, setUpdatedData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const applicationStatus = "CANCELLED";
  const dispatch = useDispatch();
  const { incomingApplications } = useSelector((state) => state.auditor);
  const { isLoading } = useSelector((state) => state.auditor);
  const [activeTab, setActiveTab] = useState(0);
  const [tableData, setTableData] = useState([]);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const navigate = useNavigate();

  const getList = async () => {
    try {
      setStatus("pending");
      const result = dispatch(getIncomingApplications(applicationStatus));
      unwrapResult(result);
      setStatus("success");
    } catch (error) {
      setStatus("failed");
      window.alert(error);
    }
  };

  const updateIncomingData = () => {
    const updatedData = incomingApplications?.map((d, index) => {
      return {
        ...d,
        srNo: index + 1,
        actions: "actions",
        dateTime: (
          <Flex width="90px" flexDirection="column">
            {moment(d?.startDate).format("DD-MM-YYYY")}
            <Box as="span" color="rgba(0,0,0,0.5)">
              {moment(d?.endDate).diff(moment(d?.startDate), "days")} days
            </Box>
          </Flex>
        ),
        auditProcesses: d?.audit_unit?.unit_processes?.map((data) => {
          return data?.name;
        }),
      };
    });

    setUpdatedData(updatedData);
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("srNo", {
      cell: (info) => info.getValue(),
      header: "srNo",
    }),
    columnHelper.accessor("audit_company.name", {
      cell: (info) => {
        const rowData = info.row.original; // Access full row data
        return (
          rowData.audit_company?.name ||
          rowData.audit_application?.name ||
          "N/A"
        );
      },
      header: "Company Name",
    }),

    columnHelper.accessor("audit_company.id", {
      cell: (info) => {
        const rowData = info.row.original; // Access full row data
        return (
          rowData.audit_company?.id || rowData.audit_application?.id || "N/A"
        );
      },
      header: "Application Number",
    }),
    columnHelper.accessor("audit_unit.unitType", {
      cell: (info) => info.getValue(),
      header: "Unit Type",
    }),
    columnHelper.accessor("id", {
      cell: (info) => info.getValue(),
      header: "Audit Number",
    }),
    columnHelper.accessor("audit_unit.name", {
      cell: (info) => info.getValue(),
      header: "Facility/Site",
      // meta: {
      //   isNumeric: true,
      // },
    }),
    columnHelper.accessor("dateTime", {
      cell: (info) => info.getValue(),
      header: "Date/Time",
      // meta: {
      //   isNumeric: true,
      // },
    }),

    columnHelper.accessor("audit_unit.address.raw_address", {
      cell: (info) => info.getValue(),
      header: "Address",
      // meta: {
      //   isNumeric: true,
      // },
    }),
    columnHelper.accessor("actions", {
      Cell: ({ cell }) => console.log(),
      header: "Actions",
      // meta: {
      //   isNumeric: true,
      // },
    }),
  ];

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    // Rank the item
    const itemRank = rankItem(row.getValue(columnId), value);
    // Store the itemRank info
    addMeta({
      itemRank,
    });
    // Return if the item should be filtered in/out
    return itemRank.passed;
  };

  const table = useReactTable({
    data: updatedData || [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  useEffect(() => {
    getList(applicationStatus);
  }, []);

  useEffect(() => {
    updateIncomingData();
  }, [incomingApplications]);

  return (
    <Box>
      {incomingApplications.length > 0 ? (
        <>
          <Box marginTop={"3rem"}>
            <Flex mb="1rem" flexDirection={"row"}>
              <Text> Auditor/ </Text>
              <Text fontWeight={"bold"}>Cancelled Applications</Text>
            </Flex>
            <Flex
              justifyContent="space-between"
              alignItems={{ base: "stretch", md: "center" }}
              direction={{ base: "column", md: "row" }}
              gap={{ base: "1rem", md: "0" }}
            >
              <Box>
                <InputGroup w={{ base: "100%", md: "auto" }}>
                  <InputLeftElement
                    height={"100%"}
                    pointerEvents="none"
                    children={<SearchIcon color="#B8DEF8" />}
                  />
                  <Input
                    type="text"
                    color={"#464C64"}
                    bg={"#fff"}
                    w={{ base: "100%", md: "500px" }}
                    h={"48px"}
                    value={globalFilter ?? ""}
                    placeholder="Search All Results"
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    fontSize={"14px"}
                    borderRadius={"10px"}
                    _placeholder={{
                      opacity: 1,
                      color: "#464C64",
                      fontSize: "14px",
                    }}
                  />
                </InputGroup>
              </Box>
              <Box>
                <Box
                  display={"flex"}
                  justifyContent="space-between"
                  flexDirection={{ base: "column", md: "row" }}
                  gap={{ base: "1rem", md: "0" }}
                >
                  <Box>
                    <Box
                      display={"flex"}
                      alignItems="center"
                      marginBottom={"10px"}
                      flexDirection={{ base: "column", md: "row" }}
                      gap={{ base: "0.5rem", md: "0" }}
                    >
                      <Box
                        as="span"
                        display="flex"
                        alignItems="center"
                        gap="5px"
                        marginRight={"10px"}
                        width={{ base: "100%", md: "auto" }}
                      >
                        <Box as="span" width={{ base: "auto", md: "30%" }}>
                          {" "}
                          Go to page:{" "}
                        </Box>
                        <Input
                          w={{ base: "100%", md: "60%" }}
                          type="number"
                          color={"#464C64"}
                          bg={"#fff"}
                          defaultValue={
                            table.getState().pagination.pageIndex + 1
                          }
                          onChange={(e) => {
                            const page = e.target.value
                              ? Number(e.target.value) - 1
                              : 0;
                            table.setPageIndex(page);
                          }}
                        />
                      </Box>
                      <Box>
                        <Select
                          bg="#fff"
                          value={table.getState().pagination.pageSize}
                          onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                          }}
                        >
                          {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                              Show {pageSize}
                            </option>
                          ))}
                        </Select>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Flex>
          </Box>
          <Box marginTop={"2rem"}>
            <Box
              as="span"
              display="flex"
              alignItems="center"
              marginBottom={"10px"}
              gap="5px"
            >
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </strong>
            </Box>
            <Tabs size="md" variant="enclosed">
              {isMobile ? (
                <Box>
                  {isLoading ? (
                    <Box>
                      {[1, 2, 3, 4].map((item) => (
                        <Box key={item} mb={4}>
                          <Skeleton height="200px" fadeDuration={1} />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box>
                      {table.getRowModel()?.rows?.map((row) => (
                        <Card
                          key={row?.id}
                          data={row?.original}
                          navigate={navigate}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              ) : (
                <TableContainer className="overflow-style" overflowX={"scroll"}>
                  <Table bg="brand.white" borderRadius="15px">
                    <Thead fontStyle={"normal"}>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                            const meta = header.column.columnDef.meta;
                            return (
                              <Th
                                key={header.id}
                                color="brand.blue"
                                textTransform="capitalize"
                                onClick={header.column.getToggleSortingHandler()}
                                isNumeric={meta?.isNumeric}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                <Box as="span" pl="1rem">
                                  {header.column.getIsSorted() ? (
                                    header.column.getIsSorted() === "desc" ? (
                                      <TriangleDownIcon
                                        aria-label="sorted descending"
                                        color={"#3958DC"}
                                        size="20"
                                      />
                                    ) : (
                                      <TriangleUpIcon
                                        aria-label="sorted ascending"
                                        color={"#3958DC"}
                                        size="20"
                                      />
                                    )
                                  ) : null}
                                </Box>
                              </Th>
                            );
                          })}
                        </Tr>
                      ))}
                    </Thead>
                    <Tbody>
                      {isLoading ? (
                        <>
                          <Tr>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                          </Tr>
                          <Tr>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                          </Tr>
                          <Tr>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                          </Tr>
                          <Tr>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                          </Tr>
                        </>
                      ) : (
                        <>
                          {table.getRowModel()?.rows?.map((row) => (
                            <Tr key={row?.id}>
                              {row.getVisibleCells().map((cell) => {
                                // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                                const meta = cell?.column?.columnDef?.meta;
                                return (
                                  <Td
                                    key={cell?.id}
                                    isNumeric={meta?.isNumeric}
                                    fontWeight={500}
                                    fontSize="sm"
                                    style={{
                                      maxWidth: "250px",
                                      whiteSpace: "normal",
                                      wordWrap: "break-word",
                                    }}
                                  >
                                    {cell?.renderValue() === "actions" ? (
                                      <Link
                                        color={"#3958DC"}
                                        fontWeight="800"
                                        onClick={() =>
                                          navigate(
                                            `/auditor/rc-form-details/${encodeURIComponent(
                                              cell?.row?.original?.id
                                            )}`
                                          )
                                        }
                                      >
                                        View Details
                                      </Link>
                                    ) : (
                                      flexRender(
                                        cell?.column?.columnDef?.cell,
                                        cell?.getContext()
                                      )
                                    )}
                                  </Td>
                                );
                              })}
                            </Tr>
                          ))}
                        </>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
              <Box
                display={"flex"}
                justifyContent="center"
                marginTop={"1rem"}
                marginBottom="2rem"
              >
                <Box display={"flex"} gap="2rem">
                  <Button
                    className="border rounded p-1"
                    bg="#52BBFF"
                    _hover={{ bg: "#52BBFF" }}
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <AiOutlineArrowLeft color="#141D43" size={18} />
                  </Button>
                  <Button
                    className="border rounded p-1"
                    bg="#52BBFF"
                    _hover={{ bg: "#52BBFF" }}
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    <AiOutlineArrowRight color="#141D43" size={18} />
                  </Button>
                </Box>
              </Box>
            </Tabs>
          </Box>
        </>
      ) : (
        <Box
          display={"flex"}
          alignItems="center"
          marginTop={"8rem"}
          justifyContent="center"
        >
          <Text fontSize={"25px"} fontWeight={"bold"} textAlign="center">
            There are no cancelled applications yet
          </Text>
        </Box>
      )}
    </Box>
  );
}

export default CancelledAplications;
